import { useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, CalendarRange, Camera, ImagePlus, Video, Clapperboard, Trash2, Pencil, X, Loader2, FileImage, Film } from "lucide-react";
import { toast } from "sonner";
import AppShell, { useAppUser } from "@/components/AppShell";
import CameraCapture from "@/components/CameraCapture";
import VideoRecorder from "@/components/VideoRecorder";
import { authApi, formatApiError } from "@/lib/api";
import { imageToDataUrl, readFileAsDataUrl } from "@/lib/files";

const CHUNK_SIZE = 7_500_000; // caracteres base64 por requisição (~5,6MB)
const VIDEO_MAX_MB = 45;

const fmtDate = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const todayIso = () => new Date().toLocaleDateString("en-CA");

function planStatus(plan, today) {
  if (today >= plan.start_date && today <= plan.end_date) return "vigente";
  if (today < plan.start_date) return "futuro";
  return "encerrado";
}

const STATUS_STYLE = {
  vigente: { label: "EM VIGOR", cls: "border-gold/50 bg-gold/10 text-gold" },
  futuro: { label: "FUTURO", cls: "border-white/20 bg-white/5 text-zinc-300" },
  encerrado: { label: "ENCERRADO", cls: "border-white/10 bg-white/[0.02] text-zinc-500" },
};

async function uploadMediaChunked(planId, file, dataUrl, onProgress) {
  const marker = ";base64,";
  const cut = dataUrl.indexOf(marker);
  const base64 = cut >= 0 ? dataUrl.slice(cut + marker.length) : dataUrl;
  const total = Math.ceil(base64.length / CHUNK_SIZE);
  const mediaId = crypto.randomUUID();
  for (let i = 0; i < total; i += 1) {
    await authApi().post(`/planos-aula/${planId}/midia`, {
      media_id: mediaId,
      name: file.name,
      media_type: file.type,
      chunk_index: i,
      total_chunks: total,
      data_base64: base64.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE),
    });
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
}

function MediaViewer({ planId, item }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const isVideo = item.media_type.startsWith("video/");

  useEffect(() => () => {
    if (data) URL.revokeObjectURL(data);
  }, [data]);

  const load = async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const r = await authApi().get(`/planos-aula/${planId}/midia/${item.id}`);
      const bin = atob(r.data.data_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      // Blob URL em vez de data URL: iOS Safari não reproduz vídeo em data URL longo
      setData(URL.createObjectURL(new Blob([bytes], { type: r.data.media_type })));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`media-item-${item.id}`}>
      {data ? (
        isVideo ? (
          <video src={data} controls className="w-full max-h-72 bg-black" data-testid={`media-video-${item.id}`} />
        ) : (
          <img src={data} alt={item.name} className="w-full max-h-72 object-contain bg-black" data-testid={`media-image-${item.id}`} />
        )
      ) : (
        <button
          type="button"
          onClick={load}
          data-testid={`media-load-${item.id}`}
          className="w-full flex flex-col items-center gap-2 py-8 text-zinc-500 hover:text-gold transition-colors"
        >
          {loading ? <Loader2 size={22} className="animate-spin" /> : isVideo ? <Film size={22} /> : <FileImage size={22} />}
          <span className="font-mono text-[10px] tracking-widest">{loading ? "CARREGANDO..." : isVideo ? "VER VÍDEO" : "VER IMAGEM"}</span>
        </button>
      )}
      <p className="px-3 py-2 font-mono text-[10px] text-zinc-500 truncate border-t border-white/10">{item.name}</p>
    </div>
  );
}

function PlanForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="rounded-2xl border border-gold/25 bg-gold/[0.04] p-5 space-y-4"
      data-testid="plan-form"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">DATA INICIAL</span>
          <input type="date" required value={form.start_date} onChange={set("start_date")} data-testid="plan-start-date"
            className="mt-1 w-full bg-obsidian border border-white/15 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] focus:border-gold/60 outline-none" />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">DATA FINAL</span>
          <input type="date" required value={form.end_date} onChange={set("end_date")} data-testid="plan-end-date"
            className="mt-1 w-full bg-obsidian border border-white/15 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] focus:border-gold/60 outline-none" />
        </label>
      </div>
      <label className="block">
        <span className="font-mono text-[10px] tracking-widest text-zinc-500">TÍTULO DA AULA</span>
        <input type="text" required minLength={2} maxLength={160} value={form.title} onChange={set("title")} data-testid="plan-title"
          placeholder="Ex.: Guarda fechada — fundamentos"
          className="mt-1 w-full bg-obsidian border border-white/15 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] focus:border-gold/60 outline-none" />
      </label>
      <label className="block">
        <span className="font-mono text-[10px] tracking-widest text-zinc-500">DESCRIÇÃO DA AULA</span>
        <textarea rows={5} maxLength={4000} value={form.description} onChange={set("description")} data-testid="plan-description"
          placeholder="Objetivos, aquecimento, técnicas, drills e finalizações do período..."
          className="mt-1 w-full bg-obsidian border border-white/15 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] focus:border-gold/60 outline-none resize-y" />
      </label>
      <div className="flex gap-3 flex-wrap">
        <button type="submit" disabled={saving} data-testid="plan-save"
          className="inline-flex items-center gap-2 font-display font-semibold bg-gold text-obsidian px-6 py-2.5 rounded-full hover:bg-gold-amber transition-colors disabled:opacity-50">
          {saving && <Loader2 size={16} className="animate-spin" />} Salvar Plano
        </button>
        <button type="button" onClick={onCancel} data-testid="plan-cancel"
          className="inline-flex items-center gap-2 font-mono text-xs tracking-widest border border-white/15 text-zinc-300 px-5 py-2.5 rounded-full hover:border-crimson/60 hover:text-crimson transition-colors">
          <X size={13} /> CANCELAR
        </button>
      </div>
    </form>
  );
}

function PlanCard({ plan, status, canManage, onEdit, onDelete, onMediaAdded }) {
  const [uploading, setUploading] = useState(null);
  const [capture, setCapture] = useState(null); // "photo" | "video" | null
  const imgGalRef = useRef(null);
  const vidGalRef = useRef(null);
  const st = STATUS_STYLE[status];

  const doUpload = async (name, type, dataUrl) => {
    setUploading({ name, progress: 0 });
    await uploadMediaChunked(plan.id, { name, type }, dataUrl, (p) => setUploading({ name, progress: p }));
    toast.success("Mídia anexada ao plano.");
    onMediaAdded();
  };

  const handleFile = async (file, kind) => {
    if (!file) return;
    try {
      const dataUrl = kind === "image" ? await imageToDataUrl(file, 1280, 0.82) : await readFileAsDataUrl(file, VIDEO_MAX_MB);
      await doUpload(file.name, kind === "image" ? "image/jpeg" : file.type, dataUrl);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.message));
    } finally {
      setUploading(null);
    }
  };

  const handleCapture = async (dataUrl, mime) => {
    setCapture(null);
    const isVideo = mime.startsWith("video/");
    const ext = mime === "image/jpeg" ? "jpg" : mime.split("/")[1] || "webm";
    const name = `${isVideo ? "video" : "foto"}-${Date.now()}.${ext}`;
    try {
      await doUpload(name, mime, dataUrl);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.message));
    } finally {
      setUploading(null);
    }
  };

  const removeMedia = async (mediaId) => {
    if (!window.confirm("Remover esta mídia do plano?")) return;
    try {
      await authApi().delete(`/planos-aula/${plan.id}/midia/${mediaId}`);
      toast.success("Mídia removida.");
      onMediaAdded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inputCls = "hidden";
  const uploadBtn = "inline-flex items-center gap-2 font-mono text-[10px] tracking-widest border border-white/15 text-zinc-300 px-4 py-2 rounded-full hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-50";

  return (
    <article className={`rounded-2xl border p-5 sm:p-6 ${status === "vigente" ? "border-gold/40 bg-gold/[0.05]" : "border-white/10 bg-white/[0.02]"}`} data-testid={`plan-card-${plan.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <span className={`inline-block font-mono text-[10px] tracking-widest border rounded-full px-3 py-1 ${st.cls}`} data-testid={`plan-status-${plan.id}`}>{st.label}</span>
          <h3 className="mt-2 font-display font-bold text-lg" data-testid={`plan-title-${plan.id}`}>{plan.title}</h3>
          <p className="mt-1 inline-flex items-center gap-2 font-mono text-[11px] text-zinc-400" data-testid={`plan-period-${plan.id}`}>
            <CalendarRange size={13} className="text-gold" /> {fmtDate(plan.start_date)} — {fmtDate(plan.end_date)}
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={() => onEdit(plan)} data-testid={`plan-edit-${plan.id}`} aria-label="Editar plano"
              className="w-9 h-9 rounded-lg border border-white/15 flex items-center justify-center text-zinc-400 hover:text-gold hover:border-gold/50 transition-colors">
              <Pencil size={15} />
            </button>
            <button type="button" onClick={() => onDelete(plan)} data-testid={`plan-delete-${plan.id}`} aria-label="Excluir plano"
              className="w-9 h-9 rounded-lg border border-crimson/40 flex items-center justify-center text-crimson hover:bg-crimson hover:text-white transition-colors">
              <Trash2 size={15} />
            </button>
          </div>
        )}
      </div>

      {plan.description && (
        <p className="mt-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-line" data-testid={`plan-description-${plan.id}`}>{plan.description}</p>
      )}

      <div className="mt-5">
        <p className="font-mono text-[10px] tracking-widest text-zinc-500 mb-3">MÍDIAS DO PLANO ({plan.media.length})</p>
        {plan.media.length === 0 && !canManage && <p className="text-sm text-zinc-600">Nenhuma mídia anexada.</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          {plan.media.map((m) => (
            <div key={m.id} className="relative">
              <MediaViewer planId={plan.id} item={m} />
              {canManage && (
                <button type="button" onClick={() => removeMedia(m.id)} data-testid={`media-delete-${m.id}`} aria-label="Remover mídia"
                  className="absolute top-2 right-2 w-7 h-7 rounded-lg bg-black/70 border border-crimson/50 flex items-center justify-center text-crimson hover:bg-crimson hover:text-white transition-colors">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>

        {canManage && (
          <div className="mt-4" data-testid={`plan-upload-${plan.id}`}>
            <input ref={imgGalRef} type="file" accept="image/*" className={inputCls} data-testid={`upload-photo-gallery-${plan.id}`}
              onChange={(e) => { handleFile(e.target.files?.[0], "image"); e.target.value = ""; }} />
            <input ref={vidGalRef} type="file" accept="video/*" className={inputCls} data-testid={`upload-video-gallery-${plan.id}`}
              onChange={(e) => { handleFile(e.target.files?.[0], "video"); e.target.value = ""; }} />
            {capture === "photo" && (
              <CameraCapture facingMode="environment" testid={`plan-photo-camera-${plan.id}`}
                onClose={() => setCapture(null)} onCapture={(dataUrl) => handleCapture(dataUrl, "image/jpeg")} />
            )}
            {capture === "video" && (
              <VideoRecorder testid={`plan-video-recorder-${plan.id}`}
                onClose={() => setCapture(null)} onCapture={(dataUrl, mime) => handleCapture(dataUrl, mime)} />
            )}
            {uploading ? (
              <div className="flex items-center gap-3 font-mono text-[11px] text-gold" data-testid={`upload-progress-${plan.id}`}>
                <Loader2 size={14} className="animate-spin" /> ENVIANDO {uploading.name}... {uploading.progress}%
              </div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={() => setCapture("photo")} className={uploadBtn} data-testid={`btn-photo-camera-${plan.id}`}><Camera size={13} /> FOTO (CÂMERA)</button>
                <button type="button" onClick={() => imgGalRef.current?.click()} className={uploadBtn} data-testid={`btn-photo-gallery-${plan.id}`}><ImagePlus size={13} /> IMAGEM</button>
                <button type="button" onClick={() => setCapture("video")} className={uploadBtn} data-testid={`btn-video-camera-${plan.id}`}><Video size={13} /> GRAVAR VÍDEO</button>
                <button type="button" onClick={() => vidGalRef.current?.click()} className={uploadBtn} data-testid={`btn-video-gallery-${plan.id}`}><Clapperboard size={13} /> VÍDEO (ARQUIVO)</button>
              </div>
            )}
            <p className="mt-2 font-mono text-[10px] text-zinc-600">CÂMERA ABRE DENTRO DO APP · VÍDEOS DE ATÉ {VIDEO_MAX_MB}MB · ATÉ 8 MÍDIAS POR PLANO</p>
          </div>
        )}
      </div>
    </article>
  );
}

export default function PlanoAula() {
  const ctx = useAppUser() || {};
  const roles = ctx.user ? ctx.user.roles || [ctx.user.role] : [];
  const canManage = roles.includes("admin") || roles.includes("responsavel");

  const [plans, setPlans] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const today = todayIso();

  const load = async () => {
    try {
      const r = await authApi().get("/planos-aula");
      setPlans(r.data.plans);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPlans([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groups = useMemo(() => {
    const g = { vigente: [], futuro: [], encerrado: [] };
    (plans || []).forEach((p) => g[planStatus(p, today)].push(p));
    g.futuro.sort((a, b) => a.start_date.localeCompare(b.start_date));
    return g;
  }, [plans, today]);

  const defaultForm = () => {
    const end = new Date();
    end.setDate(end.getDate() + 6);
    return { title: "", description: "", start_date: today, end_date: end.toLocaleDateString("en-CA") };
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await authApi().patch(`/planos-aula/${editing.id}`, form);
        toast.success("Plano de aula atualizado.");
      } else {
        await authApi().post("/planos-aula", form);
        toast.success("Plano de aula criado. Instrutores foram notificados.");
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removePlan = async (plan) => {
    if (!window.confirm(`Excluir o plano "${plan.title}" e todas as suas mídias?`)) return;
    try {
      await authApi().delete(`/planos-aula/${plan.id}`);
      toast.success("Plano excluído.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <AppShell active="material">
      <div className="relative" data-testid="plano-aula-page">
        <div className="absolute inset-0 flex items-start justify-center pt-24 pointer-events-none overflow-hidden">
          <img src="/assets/logo.jpeg" alt="" className="w-[45%] max-w-md opacity-[0.05] rounded-full" />
        </div>

        <div className="relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-gold">MATERIAL DE APOIO</p>
              <h1 className="mt-1 font-display text-2xl font-bold flex items-center gap-3" data-testid="plano-aula-title">
                <BookOpen size={22} className="text-gold" /> Plano de Aula
              </h1>
              <p className="mt-1 text-sm text-zinc-400">
                {canManage ? "Prepare a instrução da semana com descrição, imagens e vídeos." : "Veja o que o responsável técnico preparou para o período."}
              </p>
            </div>
            {canManage && !showForm && (
              <button type="button" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="plan-new-button"
                className="inline-flex items-center gap-2 font-display font-semibold bg-gold text-obsidian px-6 py-3 rounded-full hover:bg-gold-amber transition-colors">
                <BookOpen size={16} /> Novo Plano
              </button>
            )}
          </div>

          {showForm && (
            <div className="mt-6">
              <PlanForm
                initial={editing ? { title: editing.title, description: editing.description, start_date: editing.start_date, end_date: editing.end_date } : defaultForm()}
                saving={saving}
                onSave={save}
                onCancel={() => { setShowForm(false); setEditing(null); }}
              />
            </div>
          )}

          {plans === null ? (
            <p className="mt-10 text-sm text-zinc-500 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Carregando planos...</p>
          ) : plans.length === 0 && !showForm ? (
            <div className="mt-10 flex flex-col items-center gap-3 border border-dashed border-white/15 rounded-2xl py-16 text-zinc-500" data-testid="plans-empty">
              <BookOpen size={28} />
              <p className="text-sm">Nenhum plano de aula cadastrado ainda.</p>
            </div>
          ) : (
            <div className="mt-8 space-y-8">
              {groups.vigente.length > 0 && (
                <section data-testid="plans-vigente">
                  <h2 className="font-mono text-[11px] tracking-[0.25em] text-gold mb-3">PERÍODO VIGENTE</h2>
                  <div className="space-y-4">
                    {groups.vigente.map((p) => (
                      <PlanCard key={p.id} plan={p} status="vigente" canManage={canManage}
                        onEdit={(pl) => { setEditing(pl); setShowForm(true); }} onDelete={removePlan} onMediaAdded={load} />
                    ))}
                  </div>
                </section>
              )}
              {groups.futuro.length > 0 && (
                <section data-testid="plans-futuro">
                  <h2 className="font-mono text-[11px] tracking-[0.25em] text-zinc-400 mb-3">PRÓXIMOS PERÍODOS</h2>
                  <div className="space-y-4">
                    {groups.futuro.map((p) => (
                      <PlanCard key={p.id} plan={p} status="futuro" canManage={canManage}
                        onEdit={(pl) => { setEditing(pl); setShowForm(true); }} onDelete={removePlan} onMediaAdded={load} />
                    ))}
                  </div>
                </section>
              )}
              {groups.encerrado.length > 0 && (
                <section data-testid="plans-encerrado">
                  <h2 className="font-mono text-[11px] tracking-[0.25em] text-zinc-500 mb-3">HISTÓRICO</h2>
                  <div className="space-y-4">
                    {groups.encerrado.map((p) => (
                      <PlanCard key={p.id} plan={p} status="encerrado" canManage={canManage}
                        onEdit={(pl) => { setEditing(pl); setShowForm(true); }} onDelete={removePlan} onMediaAdded={load} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
